import React from "react";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import LessonsList from "./LessonsList";
import ViewHeader from "components/main-view/utils/view-header";
import {
  Course,
  Module,
  Program,
  Project,
  QuizListType,
} from "utils/interfaces";
import {
  MdOutlineFeaturedPlayList,
  MdOutlineCollectionsBookmark,
  MdOutlineAutoStories,
  MdOutlineBallot,
} from "react-icons/md";
import { LessonsContext, MainContext } from "components/utils/Contexts";
import { programLanguagesList } from "config/optionValues";
import { useNavigate } from "react-router-dom";
import {
  deleteRecordRequest,
  getCollectionRequest,
} from "services/apiRequests";
import { capitaliseFirstChar } from "utils/utils";
import { AnimatePresence } from "framer-motion";
import TabBar from "./utils/TabBar";
import Settings from "components/main-view/utils/create-edit-lesson";
import QuizPanel from "components/main-view/utils/pop-up-panels/quiz-pop-up";
import lessonInformation from "components/main-view/utils/functions/lessonInformation";
import SavedSuccessfulPanel from "components/main-view/utils/pop-up-panels/saved-successful-pop-up";
import AddRemovePanel from "components/main-view/utils/pop-up-panels/add-parent-lesson-pop-up";
import AssignLessonPanel from "components/main-view/utils/pop-up-panels/add-child-lesson-pop-up";
import ConfirmDeletionPanel from "../utils/pop-up-panels/confirm-deletion-pop-up";
import AssignClassToCoursePanel from "../utils/pop-up-panels/add-course-to-class-pop-up";
import { logoutFunction } from "services/authRequests";

interface LessonsProps {
  ref: any;
  headerRef: React.MutableRefObject<any>;
}

const Lessons: React.FC<LessonsProps> = forwardRef(({ headerRef }, ref) => {
  // States definition
  const [lessonListToShow, setLessonListToShow] = useState("programs");
  const [searchQuery, setSearchQuery] = useState("");
  const [showTabSearchBar, setShowTabSearchBar] = useState(true);
  // State used to manage the image cache
  const [version, setVersion] = useState(0);
  // States to control the list
  const [selectedData, setSelectedData] = useState<
    Program | Course | Project | Module | null
  >(null);
  // States to store the data
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [modules, setModules] = useState<Module[] | null>(null);
  const [lessonToDelete, setLessonToDelete] = useState<{
    record: Program | Course | Project | Module | null;
    lessonType: string;
  }>({ record: null, lessonType: "" });
  // Tab selection
  const [selectedTab, setSelectedTab] = useState(0);
  const [showTabs, setShowTabs] = useState(false);
  const [fromCreateNew, setFromCreateNew] = useState(false);
  const [showSavedSuccessfully, setShowSavedSuccessfully] = useState(false);
  const [showConfirmDeletePanel, setShowConfirmDeletePanel] = useState(false);
  const [showAddClasses, setShowAddClasses] = useState(false);
  const [showAddToLesson, setShowAddToLesson] = useState(false);
  const [showAddPopUp, setShowAddPopUp] = useState(false);
  const [showEditQuiz, setShowEditQuiz] = useState(false);
  const [selectedQuizToEdit, setSelectedQuizToEdit] =
    useState<QuizListType | null>(null);
  // States used for contextual creation
  const [linkedId, setLinkedId] = useState("");
  const [linkedLesson, setLinkedLesson] = useState("");
  // State for coordination lesson navigation
  const [navRegister, setNavRegister] = useState<
    {
      lessonListToShow: string;
      selectedData: Program | Course | Project | Module | null;
      record: Program | Course | Project | Module | null;
    }[]
  >([]);
  // States for add lessons to class
  const [lessonIdToAssignToClass, setLessonIdToAssignToClass] = useState("");
  // State used for the saved successful pop up
  const [successfulLesson, setSuccessfulLesson] = useState("");
  // Press back signal
  const [pressBackSignal, setPressBackSignal] = useState(false);

  // Hook used for navigation to other pages
  const navigate = useNavigate();

  // Reference to the settings module
  const settingsRef: React.Ref<any> = useRef();

  // Get context variables
  const { userSchool, userRole, setShowSideBarMenu, contextSchoolByRole } =
    useContext(MainContext);

  // Define the tab options
  const tabsOptions = [
    fromCreateNew ? "Contents" : capitaliseFirstChar(lessonListToShow),
    "Settings",
  ];

  /**
   * Define the list of functions that can be used from external components
   * using a reference
   */
  useImperativeHandle(ref, () => ({
    // Outer function to change the tab
    setLessonListToShow(lesson: string) {
      setLessonListToShow(lesson);
    },
    pressSaveButton() {
      if (settingsRef.current) settingsRef.current.handleSaveLesson();
    },
    setShowAddPopUp(bool: boolean) {
      setShowAddPopUp(bool);
    },
    setShowAddToLesson(bool: boolean) {
      setShowAddToLesson(bool);
    },
    onBackButtonClick() {
      onBackButtonClick();
    },
    startFromLastNavRegister(
      navRegisterInput: {
        lessonListToShow: string;
        selectedData: Program | Course | Project | Module | null;
        record: Program | Course | Project | Module | null;
      }[],
    ) {
      // Set the previous navRegister
      setNavRegister(navRegisterInput);

      // Navigate to the last state
      const lastRegister = navRegisterInput[navRegisterInput.length - 1];
      const recordToClick = lastRegister.record;
      const lessonList = lastRegister.lessonListToShow;

      if (recordToClick) handleCardClick(recordToClick, 0, lessonList, false);
    },
    onCreateLesson(lesson: "programs" | "courses" | "projects" | "modules") {
      onCreateLesson(lesson);
    },
  }));

  /**
   * Root definition, but also
   * Set the title and description when we have selected data
   */
  useEffect(() => {
    setHeaderTitleDescription();
  }, [selectedData, lessonListToShow, userRole]);

  /**
   * Get data logic
   */
  useEffect(() => {
    if (lessonListToShow || !showAddClasses) getData(!!selectedData);
  }, [lessonListToShow, selectedData, showAddClasses, userSchool]);

  /**
   * Function used to prevent hide the tab-search bar when coming back
   * from the editor
   */
  useEffect(() => {
    // If we are coming back as a student, then force it to be true
    if (userRole === "student") {
      setShowTabSearchBar(true);
    }
    // For the master admin case
    else if (userRole === "master admin") {
      // If we have selected data (nested type)
      if (selectedData) {
        // Show the tabs
        setShowTabs(true);

        // Set the header options
        setOptionButtons(
          lessonInformation(lessonListToShow).parent,
          selectedTab,
        );
      }
    }
  }, [userRole]);

  /**
   * Function used to control the current nav register
   */
  const addCurrentToNavRegister = () => {
    setNavRegister([
      ...navRegister,
      { lessonListToShow, selectedData, record: null },
    ]);
  };

  /**
   * Function used to create the different lessons
   */
  const onCreateLesson = (
    lesson: "programs" | "courses" | "projects" | "modules",
  ) => {
    // Set the navRegister to come back to the previous view
    addCurrentToNavRegister();
    // Hide the tab search bar (programs, courses, ...)
    setShowTabSearchBar(false);
    // Select the "settings" tab (second tab)
    setSelectedTab(1);
    // Set the state of createNew (which controls what lessonListToShow)
    setFromCreateNew(true);
    // Force the selected data to undefine the selectedData
    setSelectedData(null);
    // Set the active lesson based on the lesson selected
    setLessonListToShow(lessonInformation(lesson).child);
    // And show the content-settings tab for everything but modules
    setShowTabs(lesson !== "modules");
    // Hide the lateral bar menu
    setShowSideBarMenu(false);

    /**
     * Specific parameters (which depends of the lesson)
     */
    // Define the title and description text
    const textToDisplay = capitaliseFirstChar(lesson.slice(0, -1));
    // Define the icon to display next to the subtitle
    const displayIcon =
      lesson === "programs" ? (
        <MdOutlineFeaturedPlayList />
      ) : lesson === "courses" ? (
        <MdOutlineCollectionsBookmark />
      ) : lesson === "projects" ? (
        <MdOutlineAutoStories />
      ) : lesson === "modules" ? (
        <MdOutlineBallot />
      ) : (
        ""
      );

    // (Main Header) Specific parameters for header
    headerRef.current.setHeaderTitle(`New ${textToDisplay}`);
    headerRef.current.setHeaderDescription(
      <div className="w-fit inline-flex gap-1">
        {displayIcon}
        <div>{textToDisplay}</div>
      </div>,
    );
    // Reveal the back button
    headerRef.current.setShowBackButton(true);

    // (Lessons Header) Define behaviour
    // Hide the Add button
    headerRef.current.options().setShowAddParentLessonButton(false);
    // Hide the Add/manage button
    headerRef.current.options().setShowAddChildLessonButton(false);
    // Hide the create new button
    headerRef.current.options().setShowCreateNewButton(false);
    // And the create new pop up menu
    headerRef.current.options().setShowCreateNewPopUp(false);
    // Reveal the save button, but disabled at first
    headerRef.current.options().setSaveButton({ show: true, disabled: true });
  };

  /**
   * Function for "Create another" option
   * @param selectedLesson
   * @returns
   */
  const handleCreateAnotherLesson = (selectedLesson: string) => {
    return () => {
      // First of all, close the pop up
      setShowSavedSuccessfully(false);

      // And go to create a new lesson
      onCreateLesson(
        selectedLesson as "programs" | "courses" | "projects" | "modules",
      );
    };
  };

  /**
   * Function used to set the header title and description based
   * on the context
   */
  const setHeaderTitleDescription = (
    data = selectedData,
    lesson: string = lessonListToShow,
  ) => {
    // Check if the selected program is a program. Null means root view
    if (data) {
      // If the user is a student, then display the title and description
      // as a navigation record
      if (userRole === "student") {
        // Get the list of navigation data, to get the name of each lesson clicked
        const lessonTitles =
          navRegister.map((info, index: number) => (
            <React.Fragment key={index + 1}>
              {index > 0 && <span> / </span>}
              {/* It's "index + 1" because we are adding the "Lesson" root later */}
              <span
                className="underline cursor-pointer hover:text-neutral-800"
                onClick={() => comeBackToSpecificLesson(navRegister, index + 1)}
              >
                {info.record ? info.record.title : "-"}
              </span>
            </React.Fragment>
          )) || [];

        // Add the root lesson
        lessonTitles.unshift(
          <React.Fragment key={0}>
            <span
              className="underline cursor-pointer hover:text-neutral-800"
              onClick={() => comeBackToMainView("programs")}
            >
              Lessons
            </span>
            <span> / </span>
          </React.Fragment>,
        );

        // And set the header title and description
        headerRef.current.setHeaderTitle(capitaliseFirstChar(lesson));
        headerRef.current.setHeaderDescription(lessonTitles);
      } else {
        // Set the header title and description
        headerRef.current.setHeaderTitle(data.title);
        headerRef.current.setHeaderDescription(data.description);
      }
    } else {
      // Modify automatically for the student case
      if (userRole === "student") {
        headerRef.current.setHeaderTitle(capitaliseFirstChar(lesson));
        headerRef.current.setHeaderDescription("Lessons");
      }
      // For the rest of the roles
      else {
        // Apply this if we are not creating a new lesson
        if (!fromCreateNew) {
          headerRef.current.setHeaderTitle(capitaliseFirstChar(lesson));
          headerRef.current.setHeaderDescription(`List of current ${lesson}`);
        }
      }
    }
  };

  /**
   * Function that defines the root view
   */
  const comeBackToMainView = (
    lessonToShow = lessonListToShow,
    selectedDataInput: typeof selectedData = null,
  ) => {
    // Unselect data (in the case we selected some)
    setSelectedData(selectedDataInput);
    // Activate the side bar menu
    setShowSideBarMenu(true);
    // Hide the inner tab
    setShowTabs(false);
    // And come back to the list
    setSelectedTab(0);
    // And reset the view
    setFromCreateNew(false);
    // And set the list to display
    setLessonListToShow(lessonToShow);
    // And ensure the navigation history is empty
    setNavRegister([]);
    // Show the main tab
    setShowTabSearchBar(true);

    // (Main Header) Set the header title
    headerRef.current.setHeaderTitle(capitaliseFirstChar(lessonToShow));
    // Set the description based on the userRole
    if (userRole === "student") {
      headerRef.current.setHeaderDescription("Lessons");
    } else {
      headerRef.current.setHeaderDescription(`List of current ${lessonToShow}`);
    }
    // Delete back button
    headerRef.current.setHeaderBackButton(false);

    // (Lessons Header) And turn off the other buttons
    headerRef.current.options().setSaveButton({ show: false, disabled: true });
    // Activating the create new button
    headerRef.current.options().setShowCreateNewButton(true);
    // Remove add course
    headerRef.current.options().setShowAddParentLessonButton(false);
    // Remove add project and manage modules
    headerRef.current.options().setShowAddChildLessonButton(false);
  };

  /**
   * Function used to come back to specific lesson when clicking on a
   * description route in the student view
   * @param index Represents the position of the item clicked.
   * @returns void
   */
  const comeBackToSpecificLesson = (
    navRegisterInput: typeof navRegister,
    index: number,
  ) => {
    // Cut the rest of the navigated register
    const newRegister = navRegisterInput.slice(0, index);

    // Get the selected state that will be the new current state
    // (Equivalent to the newRegister[-1])
    const currentState = navRegisterInput[index - 1];

    // Double check that the previous state exists
    if (!currentState) return;

    // Get the current lesson info
    const currentLessonInfo = lessonInformation(currentState.lessonListToShow);

    // Unselect data (in the case we selected some)
    setSelectedData(currentState.record);
    // And set the list to display
    setLessonListToShow(currentLessonInfo.child);
    // Set the new navigation history without the previous state
    setNavRegister(newRegister);
  };

  /**
   * Function that defines the behaviour on click back button
   */
  const onBackButtonClick = () => {
    // First of all, ensure that fromCreateNew state is false
    setFromCreateNew(false);

    // Undefine the contextual Id and lesson
    setLinkedId("");
    setLinkedLesson("");

    // And delete the state that have the saved content
    setPressBackSignal(true);
    if (lessonListToShow === "programs") setPrograms(null);
    else if (lessonListToShow === "courses") setCourses(null);
    else if (lessonListToShow === "projects") setProjects(null);
    else if (lessonListToShow === "modules") setModules(null);

    // Check if the navigation history list is not empty
    if (navRegister.length <= 1) {
      if (navRegister.length === 1) {
        comeBackToMainView(
          navRegister[0].lessonListToShow,
          navRegister[0].selectedData,
        );

        return;
      }
      // Come back to the main view (based on the parent view)
      comeBackToMainView(lessonInformation(lessonListToShow).parent);
      return;
    }

    // Define the new navigation history and the previous state
    const newRegister = [...navRegister];
    // Get the last state
    const previousState = newRegister.pop();

    // Double check that the previous state exists
    if (!previousState) return;

    // Show the tabs when clicking back if there is any selected data
    setShowTabsByUserRole(
      true,
      previousState.selectedData as Program | Course | Project | Module,
    );
    // Unselect data (in the case we selected some)
    setSelectedData(previousState.selectedData);
    // And set the list to display
    setLessonListToShow(previousState.lessonListToShow);
    // Set the new navigation history without the previous state
    setNavRegister(newRegister);
    // And force set the content tab
    setSelectedTab(0);

    // Set the appropriate option buttons
    setOptionButtons(
      lessonInformation(previousState.lessonListToShow).parent,
      0,
      newRegister[newRegister.length - 1].record,
    );
    // And ensure the save button is not active
    headerRef.current.options().setSaveButton({ show: false, disabled: true });

    // And update the title and header
    setHeaderTitleDescription(previousState.selectedData);

    // Re-enable side bar menu incase not triggered above already
    // Important when leaving the create lesson screen without submitting
    setShowSideBarMenu(true);
  };

  /**
   * Function used to define what buttons should be displayed based on
   * the current lesson to show
   */
  const setOptionButtons = (
    selectedLesson: string,
    selectedTab: number,
    record?: any,
  ) => {
    // Get the lesson information
    const lessonInfo = lessonInformation(selectedLesson);

    // Define the text for the addButton/addManageButton text
    headerRef.current.options().setAddToButtonText(lessonInfo.parent);
    // Define the text for the manage button text
    headerRef.current.options().setManageButtonText(lessonInfo.child);

    // Define the allowed logic for each button
    const addChildLessonButton =
      ["programs", "courses", "projects"].includes(selectedLesson) &&
      selectedTab === 0 &&
      !!record; // force hide buttons when not created from within a selected lesson
    const addParentLessonButton =
      ["courses", "projects"].includes(selectedLesson) &&
      selectedTab === 0 &&
      !!record;

    // Apply the logic
    const enableAddChildLessonButton = record?.canEdit || false;
    headerRef.current
      .options()
      .setShowAddChildLessonButton(
        addChildLessonButton,
        enableAddChildLessonButton,
      );
    headerRef.current
      .options()
      .setShowAddParentLessonButton(addParentLessonButton);

    // Define the save button behaviour
    headerRef.current
      .options()
      .setSaveButton({ show: selectedTab === 1, disabled: true });

    // And set the settings tab in this case
    setSelectedTab((currentTab) =>
      selectedLesson === "modules" ? 1 : currentTab,
    );
  };

  const setShowTabsByUserRole = (
    bool: boolean,
    record: Program | Course | Project | Module,
  ) => {
    switch (userRole) {
      case "master admin":
        // Define this behaviour in every case
        setShowTabs(bool);
        return;

      case "customer admin":
        // For this, check that the customer admin belong to the
        // organisation of that lesson
        if (
          record.organisations &&
          record.organisations.includes(contextSchoolByRole._id)
        ) {
          // Allow define this behaviour
          setShowTabs(bool);
          return;
        }

        // Otherwise, just prevent from being shown
        return setShowTabs(false);

      case "class admin":
        // For this, check that the customer admin belong to the
        // organisation of that lesson
        if (
          record.organisations &&
          record.organisations.includes(contextSchoolByRole._id)
        ) {
          // Allow define this behaviour
          setShowTabs(bool);
          return;
        }

        // Otherwise, just prevent from being shown
        return setShowTabs(false);

      // For the rest of the users, don't allow to be changed so far
      // case "class admin":
      //   return setShowTabs(false);
      case "student":
        return setShowTabs(false);
      default:
        return setShowTabs(false);
    }
  };

  /**
   * Function applied when we press the edit button on cards
   */
  const handleCardClick = (
    record: Program | Course | Project | Module,
    selectedTab: number,
    lesson = lessonListToShow,
    addHistory: boolean = true,
  ) => {
    if (record._id === selectedData?._id) return;

    // Check if the project contain information (videoUrl, planDocument)
    // to execute it as a self-contained lesson
    if (lesson === "projects" && selectedTab === 0) {
      // Check if the course has modules inside
      const hasModules = "modules" in record && record.modules.length > 0;

      // Check if the content has been defined
      const isContentDefined =
        (!!record.planDocument ||
          ("videoUrl" in record && record.videoUrl !== "") ||
          ("finalCodeName" in record && record.finalCodeName !== "") ||
          ("initialCodeName" in record && record.initialCodeName !== "")) &&
        "language" in record &&
        ([...programLanguagesList] as string[]).includes(record.language);

      // Forward to the editor if there's no modules and the content is defined
      if (!hasModules && isContentDefined) {
        // check if we should forward to the editor
        return navigate(`/editor/projects/${record._id}`, {
          state: { navRegister },
        });
      }
    } else if (lesson === "modules" && selectedTab === 0) {
      // Check if the content has been defined
      const isContentDefined =
        "language" in record &&
        ([...programLanguagesList] as string[]).includes(record.language);

      // Forward to the editor if the content is defined
      if (isContentDefined) {
        return navigate(`/editor/modules/${record._id}`, {
          state: { navRegister },
        });
      }
    }

    // Get the lesson information
    const lessonInfo = lessonInformation(lesson);

    // Map to the child tab
    setLessonListToShow(lessonInfo.child);

    // And don't close tab search bar when it's a student (but for the rest, close it)
    setShowTabSearchBar(["student"].includes(userRole));

    // Once clicked on card, add the information to the history
    if (addHistory) {
      setNavRegister([
        ...navRegister,
        { lessonListToShow: lesson, selectedData, record: record },
      ]);
    }

    // And show tabs in every case but modules editing
    setShowTabsByUserRole(lesson !== "modules", record);

    // Make sure we are not using from "Create New" button
    setFromCreateNew(false);

    // Select the current data
    setSelectedData(record);

    // Set the selected tab based on if it's expand or edit
    setSelectedTab(selectedTab);

    // (Main Header) Update the title and description
    setHeaderTitleDescription(record, lessonInfo.child);
    // Show the back button
    headerRef.current.setHeaderBackButton(true);
    // Remove the create new button
    headerRef.current.options().setShowCreateNewButton(false);

    // Add header option buttons
    setOptionButtons(lesson, selectedTab, record);
  };

  /**
   * Expand click definition
   */
  const handleExpandClick = (record: Program | Course | Project | Module) => {
    return () => handleCardClick(record, 0);
  };

  /**
   * Edit click definition
   */
  const handleEditClick = (record: Program | Course | Project | Module) => {
    return (event: React.MouseEvent) => {
      // Prevent activate the click on the whole card
      event.stopPropagation();
      handleCardClick(record, 1);
    };
  };

  /**
   * Delete click definition
   */
  const handleDeleteClick = (
    record: Program | Course | Project | Module,
    lessonType: string,
  ) => {
    return (event: React.MouseEvent) => {
      // Prevent activate the click on the whole card
      event.stopPropagation();

      // Open the confirm delete panel
      setShowConfirmDeletePanel(true);

      // And set the lesson to delete
      setLessonToDelete({ record, lessonType });
    };
  };

  /**
   * Function executed when closing the confirm deletion pop up
   */
  const handleCloseConfirmDelete = () => {
    // Close the pop up
    setShowConfirmDeletePanel(false);

    // And reset the lesson to delete
    setLessonToDelete({ record: null, lessonType: "" });
  };

  /**
   * Function executed when confirming the deletion of the lesson
   */
  const handleClickConfirmDelete = async () => {
    // Get the delete values
    const { record, lessonType } = lessonToDelete;

    // Check if the record and the lessonType is defined
    if (!(record && lessonType)) return;

    // Execute the request
    const _id = record._id;
    const response = await deleteRecordRequest({ _id }, `/api/${lessonType}`);

    // Check if everything went right with the request
    if (!(response && response.successful)) return;

    // Force and update to the list
    getData(true, lessonType);

    // And reset the lesson to delete
    setLessonToDelete({ record: null, lessonType: "" });
  };

  /**
   * Assign to classes click definition
   */
  const handleAddToClasses = (
    record: Program | Course | Project | Module,
    lesson: string,
  ) => {
    return (event: React.MouseEvent) => {
      // Prevent activate the click on the whole card
      event.stopPropagation();
      // Show the add classes pop up
      setShowAddClasses(true);
      // And set the record to assign
      setLessonIdToAssignToClass(record._id);
    };
  };

  /**
   * Function used to get the lesson data
   */
  const getData = async (
    forceUpdate = false,
    lessonListToUpdate = lessonListToShow,
  ) => {
    // First of all, set the isLoading state
    switch (lessonListToUpdate) {
      case "programs":
        // Check if there are programs already loaded
        // (in order to prevent re-loading)
        if (!forceUpdate && programs && programs.length > 0) return;
        setPrograms(null);
        break;
      case "courses":
        // Check if there are courses already loaded
        // (in order to prevent re-loading)
        if (!forceUpdate && courses && courses.length > 0) return;
        setCourses(null);
        break;
      case "projects":
        // Check if there are projects already loaded
        // (in order to prevent re-loading)
        if (!forceUpdate && projects && projects.length > 0) return;
        setProjects(null);
        break;
      case "modules":
        // Check if there are modules already loaded
        // (in order to prevent re-loading)
        if (!forceUpdate && modules && modules.length > 0) return;
        setModules(null);
        break;
    }

    // If there is a selected data, get the ids to show
    let response;
    if (selectedData) {
      // Get all the child lesson that contains the id of the selected data
      response = await getCollectionRequest(
        `/api/${lessonListToUpdate}/from-parent?parent=${selectedData._id}`,
      );
    } else {
      // Just collect the response without filtering
      response = await getCollectionRequest(`/api/${lessonListToUpdate}`);
    }

    // Check if the response was right
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // And set to the corresponding state
    if (lessonListToUpdate === "programs") {
      setPrograms(data);
    } else if (lessonListToUpdate === "courses") {
      setCourses(data);
    } else if (lessonListToUpdate === "projects") {
      setProjects(data);
    } else if (lessonListToUpdate === "modules") {
      setModules(data);
    }

    return data;
  };

  /**
   * Function executed when clicking on the lessons list tab
   * @param index
   */
  const handleClickTab = (index: number) => {
    // Change the tab
    setSelectedTab(index);

    // Execute the header button options
    setOptionButtons(lessonInformation(lessonListToShow).parent, index);
  };

  /**
   * Definition of the function executed after creating a lesson
   * in the database
   */
  const afterCreateLessons = (
    successfulLesson: string,
    successfulOperation: boolean = true,
  ) => {
    // Show create successful pop up message
    setShowSavedSuccessfully(successfulOperation);

    // Define the name of the lesson to show in the saved successful pop up panel
    setSuccessfulLesson(successfulLesson);

    // Defining the parent as lesson list to show
    if (successfulOperation) {
      setLessonListToShow(lessonInformation(lessonListToShow).parent);
    }

    // Update data
    getData(true, successfulLesson);

    // Update all of the lessons
    setPrograms(null);
    setCourses(null);
    setProjects(null);
    setModules(null);

    // And come back to the previous view menu
    if (successfulOperation) onBackButtonClick();

    // Re-enable side bar menu and header option buttons
    // Important after creating a lesson from the "This area is empty" view
    setShowSideBarMenu(true);
    const lesson = navRegister[navRegister.length - 1];
    setOptionButtons(
      lessonInformation(lesson.lessonListToShow).parent,
      0,
      lesson.selectedData,
    );

    // if (!fromCreateNew) onBackButtonClick();
    // else comeBackToMainView(lessonInformation(lessonListToShow).parent);
  };

  /**
   * Auxiliar function used to refresh a whole lesson list
   * @param lessonType
   * @returns
   */
  const refreshData = async (lessonType: string) => {
    // If there is a selected data, get the ids to show
    const response = await getCollectionRequest(`/api/${lessonType}`);

    // Check if the response was right
    if (!response.successful) return;

    // Get the data
    const data = response.content;

    // And set to the corresponding state
    if (lessonType === "programs") {
      setPrograms(data);
    } else if (lessonType === "courses") {
      setCourses(data);
    } else if (lessonType === "projects") {
      setProjects(data);
    } else if (lessonType === "modules") {
      setModules(data);
    }

    return data;
  };

  return (
    <LessonsContext.Provider
      value={{
        version,
        setVersion,
        setLinkedId,
        setLinkedLesson,
        onCreateLesson,
        selectedData,
        setSelectedData,
        refreshData,
      }}
    >
      <div className="w-full self-stretch flex-col justify-start items-start gap-4 inline-flex">
        {showTabSearchBar && (
          <ViewHeader
            searchQuery={searchQuery}
            activeItem={lessonListToShow}
            tabOptions={["Programs", "Courses", "Projects", "Modules"]}
            showTabOptions={[
              "master admin",
              "customer admin",
              "class admin",
            ].includes(userRole)}
            setActiveItem={setLessonListToShow}
            setSearchQuery={setSearchQuery}
            headerRef={headerRef}
          />
        )}
        <div className="w-full h-full flex-col justify-start items-start gap-4 inline-flex overflow-x-hidden">
          {showTabs && (
            <TabBar
              options={tabsOptions}
              selectedTab={selectedTab}
              handleClickTab={handleClickTab}
            />
          )}
          {selectedTab === 0 ? (
            <LessonsList
              lessonListToShow={lessonListToShow}
              selectedData={selectedData}
              programs={programs}
              courses={courses}
              projects={projects}
              modules={modules}
              searchQuery={searchQuery}
              navRegister={navRegister}
              handleExpandClick={handleExpandClick}
              handleEditClick={handleEditClick}
              handleDeleteClick={handleDeleteClick}
              handleAddToClasses={handleAddToClasses}
              setLinkedId={setLinkedId}
              setLinkedLesson={setLinkedLesson}
              setShowEditQuiz={setShowEditQuiz}
              setSelectedQuizToEdit={setSelectedQuizToEdit}
              showEditQuiz={showEditQuiz}
              pressBackSignal={pressBackSignal}
              setPressBackSignal={setPressBackSignal}
              onCreateLesson={onCreateLesson}
            />
          ) : (
            <Settings
              ref={settingsRef}
              lessonListToShow={lessonListToShow}
              selectedLesson={lessonInformation(lessonListToShow).parent}
              linkedId={linkedId}
              linkedLesson={linkedLesson}
              afterCreateLessons={afterCreateLessons}
              headerRef={headerRef}
            />
          )}
        </div>
        {/* Pop up panels */}
        <AnimatePresence>
          {/* Add pop-up */}
          {showAddPopUp && (
            <AddRemovePanel
              key="add-remove-lesson-panel"
              selectedData={selectedData}
              selectedLesson={lessonListToShow}
              setShowPopUp={setShowAddPopUp}
              setLinkedId={setLinkedId}
              setLinkedLesson={setLinkedLesson}
              setSelectedData={setSelectedData}
              onCreateLesson={onCreateLesson}
            />
          )}
          {/* Saved successful message pop-up */}
          {showSavedSuccessfully && (
            <SavedSuccessfulPanel
              key="saved-successful-panel"
              title={`${capitaliseFirstChar(
                successfulLesson.slice(0, -1),
              )} saved successfully`}
              paragraphs={[
                `The ${successfulLesson.slice(
                  0,
                  -1,
                )} details you entered have been saved without a hitch.`,
                "This is a significant milestone in crafting a cohesive and engaging learning experience.",
              ]}
              createText="Create another"
              onCreateAnother={handleCreateAnotherLesson(successfulLesson)}
              onClose={() => setShowSavedSuccessfully(false)}
            />
          )}
          {/* Assign to class pop-up as customer admin */}
          {showAddClasses && (
            <AssignClassToCoursePanel
              key="assign-class-to-course-panel"
              courseIdToAssign={lessonIdToAssignToClass}
              onClose={() => setShowAddClasses(false)}
            />
          )}
          {/* Assign to lesson pop-up */}
          {showAddToLesson && selectedData?._id && (
            <AssignLessonPanel
              key="assign-lesson-panel"
              selectedDataId={selectedData?._id || ""}
              lessonListToShow={lessonInformation(lessonListToShow).parent}
              setShowPopUp={setShowAddToLesson}
              onCreateLesson={onCreateLesson}
            />
          )}
          {/* Edit quiz pop-up */}
          {showEditQuiz && (
            <QuizPanel
              key="edit-quiz-panel"
              type="edit-quiz"
              quizzes={[]}
              selectedLessonData={selectedData}
              selectedQuizToEdit={selectedQuizToEdit}
              setShowPopUp={setShowEditQuiz}
              setQuizzes={() => {}}
            />
          )}
          {/* Confirm delete lesson */}
          {showConfirmDeletePanel && (
            <ConfirmDeletionPanel
              key="confirm-lesson-deletion-panel"
              title="Confirm Deletion"
              paragraphs={[
                "This action cannot be undone.",
                "All related data will be permanently removed from the system.",
              ]}
              deleteText="Delete"
              onDelete={handleClickConfirmDelete}
              onClose={handleCloseConfirmDelete}
            />
          )}
        </AnimatePresence>
      </div>
    </LessonsContext.Provider>
  );
});

export default Lessons;
